import React, {useState} from "react";
import {useMount} from "ahooks";
import {VscsApi} from "../../services/VscsApi";
import {Card, Col, DatePicker, Divider, Row, Space, Spin, Tag, Typography} from "antd";
import moment from "moment";
import {Column, Pie} from "@ant-design/plots";
import {ValueUtils} from "../../utils/ValueUtils";

const {Title, Text, Paragraph} = Typography

const VenueFlowBar = ({data}) => {
    const config = {
        data,
        isStack: true,
        xField: 'venue',
        yField: 'value',
        seriesField: 'type',
        label: {
            // 可手动配置 label 数据标签位置
            position: 'middle',
            // 'top', 'bottom', 'middle'
            // 可配置附加的布局方法
            layout: [
                // 柱形图数据标签位置自动调整
                {
                    type: 'interval-adjust-position',
                }, // 数据标签防遮挡
                {
                    type: 'interval-hide-overlap',
                }, // 数据标签文颜色自动调整
                {
                    type: 'adjust-color',
                },
            ],
        },
    };

    return (
        <Column {...config} />
    )
}

const ReportPie = ({data = []}) => {
    const config = {
        appendPadding: 10,
        data: data.filter(d => d.count !== "0").map(d => {
            return {...d, count: Number(d.count)}
        }),
        angleField: 'count',
        colorField: 'type',
        radius: 0.9,
        label: {
            type: 'inner',
            offset: '-30%',
            content: ({percent}) => `${(percent * 100).toFixed(0)}%`,
            style: {
                fontSize: 14,
                textAlign: 'center',
            },
        },
        interactions: [
            {
                type: 'element-active',
            },
        ],
    };

    return <Pie {...config} />;
}

const GenderTrendBar = ({data}) => {
    const config = {
        data: data.map(d => {
            return {
                ...d,
                count: Number(d.count)
            }
        }),
        isStack: true,
        xField: 'time',
        yField: 'count',
        seriesField: 'type',
        label: {
            // 可手动配置 label 数据标签位置
            position: 'middle',
            // 'top', 'bottom', 'middle'
            // 可配置附加的布局方法
            layout: [
                // 柱形图数据标签位置自动调整
                {
                    type: 'interval-adjust-position',
                }, // 数据标签防遮挡
                {
                    type: 'interval-hide-overlap',
                }, // 数据标签文颜色自动调整
                {
                    type: 'adjust-color',
                },
            ],
        },
    };

    return (
        <>
            <Column {...config} />
        </>
    )
}

const VscsDailyReportPage = () => {
    const [loading, setLoading] = useState(true)
    const [venueCount, setVenueCount] = useState({})
    const [report, setReport] = useState({})
    const [date, setDate] = useState(moment(new Date() - 24 * 60 * 60 * 1000))
    const [flowData, setFlowData] = useState([])

    const requestData = (date) => {
        setLoading(true)
        VscsApi.reportDaily(ValueUtils.formatDateOrEmpty(date, "YYYYMMDD"))
            .onSuccess(setReport)
            .onComplete(() => setLoading(false))
        VscsApi.flow(date, date)
            .onSuccess(_data => {
                const d = _data.map(d => {
                    return {
                        ...d,
                        value: Number(d.value)
                    }
                })
                setFlowData([...d])
            })
    }

    useMount(() => {
        setLoading(true)
        VscsApi.venueCount()
            .onSuccess(setVenueCount)
        requestData(date)
    })

    const handleDateChange = (newDate) => {
        setDate(newDate)
        requestData(newDate)
    }

    return (
        <>
            <Spin spinning={loading}>
                <Card>
                    <Title level={3}>{venueCount.master?.left.name}宗教场所视频安防日报</Title>
                    <DatePicker allowClear={false} bordered={false} defaultValue={date} onChange={handleDateChange}/>

                    <Divider/>

                    <Title
                        level={5}>{venueCount.master?.left.name}共有宗教场所 {venueCount.master?.right?.left ?? '-'} 个：</Title>
                    <Space split={"，"} wrap>
                        {
                            venueCount.master?.right?.right?.map(religionVenueCountPair =>
                                <Text>{religionVenueCountPair.left} ：<strong>{religionVenueCountPair.right}</strong> 个</Text>)
                        }
                    </Space>。

                    <Divider dashed/>

                    <Title level={5}>流量统计</Title>
                    <Paragraph>今日综合人流量：<strong>{report.total?.count}</strong> 人次，相比昨日<strong>{report.total?.diff}</strong></Paragraph>

                    <Row>
                        <Col span={16}>
                            <Card size="small">
                                <Text>今日分时综合流量</Text>
                                <GenderTrendBar data={report.totalHourList ?? []}/>
                            </Card>
                        </Col>
                        <Col span={4}>
                            <Card size="small">
                                <Text>今日流量性别占比</Text>
                                <ReportPie data={report.totalGenderPie ?? []}/>
                            </Card>
                        </Col>
                        <Col span={4}>
                            <Card size="small">
                                <Text>今日流量年龄占比</Text>
                                <ReportPie data={report.totalAgePie ?? []}/>
                            </Card>
                        </Col>
                    </Row>

                    <Divider dashed/>
                    <Title level={5}>以下为各场所信息</Title>

                    <VenueFlowBar data={flowData}/>
                    <br/>

                    {
                        report.venues?.map(venue => <>
                            <Divider orientation="left" plain>
                                {venue.name} 【{venue.properties?.religion}】 【{venue.properties?.region?.name}】
                            </Divider>
                            {
                                venue.properties?.sort == "0" && <Paragraph>无数据</Paragraph>
                            }
                            {
                                venue.properties?.sort != "0" &&
                                <Paragraph>今日综合人流量：<strong>{venue.properties?.total?.count}</strong> 人次，相比昨日<strong>{venue.properties?.total?.diff}</strong></Paragraph>
                            }
                            {
                                venue.properties?.sort != "0" && <Row>
                                    <Col span={16}>
                                        <Card size="small">
                                            <Text>今日分时综合流量</Text>
                                            <GenderTrendBar data={venue.properties?.totalHourList ?? []}/>
                                        </Card>
                                    </Col>
                                    <Col span={4}>
                                        <Card size="small">
                                            <Text>今日流量性别占比</Text>
                                            <ReportPie data={venue.properties?.totalGenderPie ?? []}/>
                                        </Card>
                                    </Col>
                                    <Col span={4}>
                                        <Card size="small">
                                            <Text>今日流量年龄占比</Text>
                                            <ReportPie data={venue.properties?.totalAgePie ?? []}/>
                                        </Card>
                                    </Col>
                                </Row>
                            }
                        </>)
                    }
                </Card>
            </Spin>
        </>
    )
}

export default VscsDailyReportPage